import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
    getPendingCount, 
    getPendingActions, 
    forceSync, 
    clearSyncedActions,
    createOfflineAPI 
} from '../lib/offlineSync';
import { useAuth } from './AuthContext';
import { API_URL } from '../lib/utils';

const OfflineContext = createContext(null);

export const useOffline = () => {
    const context = useContext(OfflineContext);
    if (!context) {
        throw new Error('useOffline must be used within an OfflineProvider');
    }
    return context;
};

export const OfflineProvider = ({ children }) => {
    const { token } = useAuth();
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [pendingCount, setPendingCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncResult, setLastSyncResult] = useState(null);

    // Create offline API instance
    const offlineAPI = React.useMemo(() => {
        return createOfflineAPI(API_URL, token);
    }, [token]);

    // Update pending count
    const updatePendingCount = useCallback(async () => {
        try {
            const count = await getPendingCount();
            setPendingCount(count);
        } catch (error) {
            console.error('[Offline] Error getting pending count:', error);
        }
    }, []);

    // Handle online/offline events
    useEffect(() => {
        const handleOnline = () => {
            console.log('[Offline] Back online');
            setIsOnline(true);
        };

        const handleOffline = () => {
            console.log('[Offline] Gone offline');
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Auto-sync when back online
    useEffect(() => {
        if (isOnline && pendingCount > 0) {
            handleSync();
        }
    }, [isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

    // Listen for service worker messages
    useEffect(() => {
        if (!navigator.serviceWorker) return;

        const handleMessage = (event) => {
            const { data } = event;
            
            if (data.type === 'SYNC_COMPLETE') {
                console.log('[Offline] Sync complete:', data);
                setIsSyncing(false);
                setLastSyncResult({
                    success: data.success,
                    failed: data.failed,
                    timestamp: new Date().toISOString(),
                });
                updatePendingCount();
                // Clear synced actions after successful sync
                if (data.success > 0) {
                    clearSyncedActions();
                }
            }

            if (data.type === 'SYNC_SUCCESS') {
                console.log('[Offline] Action synced:', data.action);
                updatePendingCount();
            }

            if (data.type === 'PENDING_COUNT') {
                setPendingCount(data.count);
            }
        };

        navigator.serviceWorker.addEventListener('message', handleMessage);

        return () => {
            navigator.serviceWorker.removeEventListener('message', handleMessage);
        };
    }, [updatePendingCount]);

    // Initial pending count
    useEffect(() => {
        updatePendingCount();
    }, [updatePendingCount]);

    // Manual sync trigger
    const handleSync = useCallback(async () => {
        if (!isOnline) {
            console.log('[Offline] Cannot sync while offline');
            return false;
        }

        setIsSyncing(true);
        const success = await forceSync();
        
        if (!success) {
            // If service worker sync failed, try direct sync
            try {
                const pending = await getPendingActions();
                console.log(`[Offline] Manual sync of ${pending.length} actions`);
                // The actual sync will be handled by the service worker
                setTimeout(() => {
                    setIsSyncing(false);
                    updatePendingCount();
                }, 2000);
            } catch (error) {
                console.error('[Offline] Manual sync error:', error);
                setIsSyncing(false);
            }
        }

        return success;
    }, [isOnline, updatePendingCount]);

    const value = {
        isOnline,
        pendingCount,
        isSyncing,
        lastSyncResult,
        offlineAPI,
        sync: handleSync,
        refreshPendingCount: updatePendingCount,
    };

    return (
        <OfflineContext.Provider value={value}>
            {children}
        </OfflineContext.Provider>
    );
};

export default OfflineContext;
